import { Link, useRouterState } from '@tanstack/react-router';
import {
  Home, ShoppingBag, ShoppingCart, Wallet, BarChart3, Warehouse, Factory,
  Briefcase, Users, UserSquare, ClipboardList, Headphones, Settings, HelpCircle,
  BookOpen, ChevronsLeft, Star, Plus, Building2, ChevronsUpDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { useUI } from '@/store/ui';
import { useMyPermissions } from '@/lib/permissions';
import { useStarredMenu } from '@/lib/starredMenu';
import { doctypes } from '@/lib/doctypes';
import { Tooltip } from '@/components/Tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/DropdownMenu';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  shortcut?: string;
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

// requireAny: the user needs read access to AT LEAST ONE of these doctypes
// for the menu item to show. Items without `requireAny` are always visible
// (e.g. Home). Module umbrella items (e.g. /accounting) list every doctype
// that lives under that module — a Manager who can only read `customer`
// still sees /accounting because it has something inside.
type GuardedNavItem = NavItem & { requireAny?: string[] };

const sections: { label?: string; items: GuardedNavItem[] }[] = [
  {
    items: [
      { to: '/', label: 'Home', icon: Home, shortcut: 'G H' },
    ],
  },
  {
    label: 'Modules',
    items: [
      { to: '/accounting',     label: 'Finance',            icon: Wallet,         requireAny: ['sales_invoice','purchase_invoice','journal_entry','payment_entry','customer','supplier','item','tax_template','account'] },
      { to: '/stock',          label: 'Stock',              icon: Warehouse,      requireAny: ['warehouse'] },
      { to: '/buying',         label: 'Procurement',        icon: ShoppingBag,    requireAny: ['material_request','purchase_order','purchase_receipt','purchase_invoice','supplier'] },
      { to: '/selling',        label: 'Sales',              icon: BarChart3,      requireAny: ['sales_invoice','customer','lead'] },
      { to: '/pos/invoices',   label: 'POS',                icon: ShoppingCart,   requireAny: ['pos_invoice','pos_profile'] },
      { to: '/manufacturing',  label: 'Production',         icon: Factory,        requireAny: ['bom','work_order'] },
      { to: '/projects',       label: 'Operations',         icon: Briefcase,      requireAny: ['project'] },
      { to: '/crm',            label: 'CRM',                icon: UserSquare,     requireAny: ['lead','customer'] },
      { to: '/hr',             label: 'HR & Payroll',       icon: Users,          requireAny: ['employee'] },
      { to: '/assets',         label: 'Asset & Inventory',  icon: ClipboardList,  requireAny: ['asset'] },
      { to: '/support',        label: 'Helpdesk',           icon: Headphones,     requireAny: ['issue'] },
    ],
  },
];

export function Sidebar() {
  const sidebarCollapsed = useUI((s) => s.sidebarCollapsed);
  return (
    <aside
      className={cn(
        // sticky + self-start pins the sidebar to the viewport so it stays
        // full-height while only the page content scrolls — otherwise the
        // h-screen aside scrolls away with the document on tall pages.
        'shrink-0 sticky top-0 self-start h-screen flex flex-col bg-canvas border-r border-hairline transition-[width] duration-150 ease-gentle',
        sidebarCollapsed ? 'w-[64px]' : 'w-[240px]',
      )}
    >
      <SidebarContent collapsed={sidebarCollapsed} showCollapseToggle />
    </aside>
  );
}

/**
 * Inner nav content — workspace switcher + nav sections + footer. Rendered
 * both inside the desktop <aside> and inside the mobile MobileDrawer. The
 * drawer always passes `collapsed=false` and omits the collapse toggle.
 */
export function SidebarContent({
  collapsed,
  showCollapseToggle,
}: {
  collapsed: boolean;
  showCollapseToggle?: boolean;
}) {
  const toggleSidebar = useUI((s) => s.toggleSidebar);
  const router = useRouterState();
  const path = router.location.pathname;
  const { t: _t } = useTranslation();
  const perms = useMyPermissions();
  const { items: stars } = useStarredMenu();

  // Filter each section's items by the caller's read perms. Drop sections
  // that end up empty — a label with zero items is just noise.
  const visibleSections = sections
    .map((s) => ({
      ...s,
      items: s.items.filter((it) => !it.requireAny || perms.canRead_any(it.requireAny)),
    }))
    .filter((s) => s.items.length > 0);

  // Build the dynamic Starred section from the user's saved stars. Icon
  // resolution: prefer the matching DoctypeConfig.icon (so the sidebar
  // looks like the page header); fall back to a generic Star.
  const starredSection: { label: string; items: NavItem[] } | null = stars.length === 0 ? null : {
    label: 'Starred',
    items: stars.map((s) => ({
      to: s.path,
      label: s.label,
      icon: iconForPath(s.path),
    })),
  };

  return (
    <>
      {/* App brand — logogram when expanded (the chunky leaf-L mark reads
       *  cleanly at 28px; the full wordmark's "erp" subscript would be
       *  unreadable at this height). Collapsed sidebar shows the workspace
       *  switcher's square mark below; no logogram there. */}
      {!collapsed && (
        <div className="px-4 pt-4 pb-1">
          <img
            src="/logica-erp-logogram.png"
            alt="Logica ERP"
            className="h-7 w-auto select-none dark:hidden"
            draggable={false}
          />
          <img
            src="/logica-erp-logogram-white.png"
            alt="Logica ERP"
            className="h-7 w-auto select-none hidden dark:block"
            draggable={false}
          />
        </div>
      )}

      {/* Workspace switcher — placeholder for multi-tenant; only shown when
       *  the rail is expanded so the collapsed 64px column stays purely
       *  navigational. */}
      {!collapsed && (
        <div className="px-3 pt-2 pb-2">
          <WorkspaceSwitcher />
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-5">
        {starredSection && (
          <div>
            {!collapsed && (
              <div className="px-2.5 pt-1 pb-1.5 flex items-center gap-2 text-stone text-micro-uppercase">
                <Star className="size-3" />
                {starredSection.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {starredSection.items.map((item) => {
                const active = path === item.to || (item.to !== '/' && path.startsWith(item.to));
                const link = (
                  <Link
                    to={item.to as never}
                    className={cn(
                      'relative flex items-center gap-2 px-2.5 h-11 md:h-9 rounded-md text-[13px] transition-colors',
                      active
                        ? 'bg-surface text-ink font-medium'
                        : 'text-steel hover:bg-surface hover:text-ink',
                      collapsed && 'justify-center px-0',
                    )}
                  >
                    {active && !collapsed && (
                      <span className="absolute left-0 size-1.5 rounded-full bg-brand-green -ml-0.5" aria-hidden />
                    )}
                    <item.icon className={cn('size-4 shrink-0', active && 'text-ink')} />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </Link>
                );
                return (
                  <li key={item.to} className="group relative">
                    {collapsed ? <Tooltip side="right" content={item.label}>{link}</Tooltip> : link}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {visibleSections.map((section, sIdx) => (
          <div key={sIdx}>
            {section.label && !collapsed && (
              <div className="px-2.5 pt-1 pb-1.5 flex items-center gap-2 text-stone text-micro-uppercase">
                {section.label === 'Starred' && <Star className="size-3" />}
                {section.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active = path === item.to || (item.to !== '/' && path.startsWith(item.to));
                const link = (
                  <Link
                    to={item.to}
                    className={cn(
                      'relative flex items-center gap-2 px-2.5 h-11 md:h-9 rounded-md text-[13px] transition-colors',
                      active
                        ? 'bg-surface text-ink font-medium'
                        : 'text-steel hover:bg-surface hover:text-ink',
                      collapsed && 'justify-center px-0',
                    )}
                  >
                    {/* Mint dot indicator on the leading edge when active */}
                    {active && !collapsed && (
                      <span className="absolute left-0 size-1.5 rounded-full bg-brand-green -ml-0.5" aria-hidden />
                    )}
                    <item.icon className={cn('size-4 shrink-0', active && 'text-ink')} />
                    {!collapsed && (
                      <>
                        <span className="truncate">{item.label}</span>
                        {item.shortcut && (
                          <span className="ml-auto kbd opacity-0 group-hover:opacity-100 transition-opacity">{item.shortcut}</span>
                        )}
                      </>
                    )}
                  </Link>
                );
                return (
                  <li key={item.to} className="group relative">
                    {collapsed ? <Tooltip side="right" content={item.label}>{link}</Tooltip> : link}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-2 py-2 border-t border-hairline space-y-0.5">
        {[
          { to: '/settings',   label: 'Settings',   icon: Settings },
          { to: '/user-guide', label: 'User Guide', icon: BookOpen },
          { to: '/help',       label: 'Help',       icon: HelpCircle },
        ].map((it) => (
          <Link
            key={it.to}
            to={it.to}
            className={cn(
              'relative flex items-center gap-2 px-2.5 h-11 md:h-9 rounded-md text-[13px] text-steel hover:bg-surface hover:text-ink transition-colors',
              collapsed && 'justify-center px-0',
            )}
          >
            <it.icon className="size-4 shrink-0" />
            {!collapsed && <span className="truncate">{it.label}</span>}
          </Link>
        ))}
        {showCollapseToggle && (
          <button
            type="button"
            onClick={toggleSidebar}
            className={cn(
              'relative flex items-center gap-2 px-2.5 h-11 md:h-9 w-full rounded-md text-[13px] text-stone hover:bg-surface hover:text-ink transition-colors',
              collapsed && 'justify-center px-0',
            )}
            aria-label="Toggle sidebar"
          >
            <ChevronsLeft className={cn('size-4 shrink-0 transition-transform', collapsed && 'rotate-180')} />
            {!collapsed && <span className="truncate text-caption">Collapse</span>}
          </button>
        )}
      </div>
    </>
  );
}

// Resolve the icon for a starred path by matching against the DoctypeConfig
// registry. Falls back to the Star icon for paths that aren't ListView pages
// (rare today but possible if a user stars a custom URL later).
function iconForPath(p: string): React.ComponentType<{ className?: string }> {
  for (const d of Object.values(doctypes)) {
    if (`${d.modulePath}/${d.slug}` === p) return d.icon;
  }
  return Star;
}

function WorkspaceSwitcher() {
  const brand = useUI((s) => s.brand);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex items-center gap-2 w-full p-1.5 rounded-md hover:bg-surface transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green/40',
        )}
      >
        <div className="flex-1 min-w-0 text-left">
          <div className="text-body-sm font-medium text-ink truncate">{brand.name}</div>
          <div className="text-caption text-stone truncate">{brand.tagline}</div>
        </div>
        <ChevronsUpDown className="size-3.5 text-stone shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="start" className="min-w-[220px]">
        <div className="px-2.5 py-1.5 text-caption text-stone">Companies</div>
        <DropdownMenuItem>
          <Building2 className="size-4 text-stone" />
          <span>{brand.name}</span>
          <span className="ml-auto text-caption text-brand-green">● Active</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          <Plus className="size-4 text-stone" />
          <span>Create company</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
